/** Roles that may use the full application (all routes). Canonical names; matching is case-insensitive. */
export const FULL_DASHBOARD_ACCESS_ROLES = ['Admin', 'SuperAdmin'] as const;

/** Roles restricted to School Profile Dashboard only (plus login/logout). */
export const SCHOOL_PROFILE_ONLY_ROLES = ['SchoolAdmin', 'ParentRole'] as const;

/** Any of these roles may sign in past RequireAuth. `Parent` is a common backend alias for parent-facing users. */
export const LOGIN_ALLOWED_ROLES = [
  ...FULL_DASHBOARD_ACCESS_ROLES,
  ...SCHOOL_PROFILE_ONLY_ROLES,
  'Parent',
] as const;

export type SchoolProfileViewMode = 'mehe' | 'school' | 'parent';

/** Normalize for comparisons (case, spacing). */
export function normRoleKey(r: string): string {
  return r.trim().toLowerCase().replace(/\s+/g, '');
}

const FULL_KEYS = new Set(
  (FULL_DASHBOARD_ACCESS_ROLES as readonly string[]).map((x) => normRoleKey(x))
);
const LOGIN_KEYS = new Set(
  (LOGIN_ALLOWED_ROLES as readonly string[]).map((x) => normRoleKey(x))
);

export function hasFullDashboardAccess(roles: string[] | undefined): boolean {
  if (!roles?.length) return false;
  return roles.some((r) => FULL_KEYS.has(normRoleKey(r)));
}

export function hasSchoolProfileOnlyAccess(roles: string[] | undefined): boolean {
  if (!roles?.length) return false;
  if (hasFullDashboardAccess(roles)) return false;
  return roles.some((r) => LOGIN_KEYS.has(normRoleKey(r)));
}

export function hasAnyLoginRole(roles: string[] | undefined): boolean {
  if (!roles?.length) return false;
  return roles.some((r) => {
    const k = normRoleKey(r);
    return LOGIN_KEYS.has(k);
  });
}

/**
 * Which School Profile Dashboard presentation to show.
 * Admin/SuperAdmin → MEHE internal; SchoolAdmin → school; ParentRole / Parent → parent.
 */
export function getSchoolProfileViewMode(roles: string[] | undefined): SchoolProfileViewMode {
  if (hasFullDashboardAccess(roles)) return 'mehe';
  if (roles?.some((r) => normRoleKey(r) === 'schooladmin')) return 'school';
  return 'parent';
}

/** Publication rule shown on governed reports: driven by signed-in role, not backend text. */
export function getSchoolProfilePublicationRuleLabel(roles: string[] | undefined): string {
  const mode = getSchoolProfileViewMode(roles);
  if (mode === 'mehe') return 'MEHE version';
  if (mode === 'school') return 'School version';
  return 'Parent-facing version';
}

export function userHasRoleCaseInsensitive(
  userRoles: string[] | undefined,
  role: string
): boolean {
  const k = normRoleKey(role);
  return userRoles?.some((r) => normRoleKey(r) === k) ?? false;
}

export function userHasAnyRoleCaseInsensitive(
  userRoles: string[] | undefined,
  roles: string[]
): boolean {
  return roles.some((role) => userHasRoleCaseInsensitive(userRoles, role));
}
